import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { PageHeader } from "../components/Common";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Check, Sparkles, Clock, CheckCircle2, XCircle, PauseCircle } from "lucide-react";
import { formatCurrency } from "../lib/currency";
import { useDataSync } from "../lib/realtime";

const STATUS = {
  pending: { label: "Pending approval", color: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  approved: { label: "Active", color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "bg-rose-50 text-rose-700 border-rose-200", icon: XCircle },
  suspended: { label: "Suspended", color: "bg-slate-100 text-slate-600 border-slate-200", icon: PauseCircle },
  expired: { label: "Expired", color: "bg-slate-100 text-slate-500 border-slate-200", icon: Clock },
};

export default function Subscribe() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [form, setForm] = useState({ institute_name: "", owner_name: user?.name || "", mobile: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const [p, h] = await Promise.all([
      api.get("/payments/plans"),
      api.get("/subscriptions/mine"),
    ]);
    setPlans(p.data);
    setHistory(h.data);
  };
  useEffect(() => { load(); }, []);
  useDataSync("subscriptions", () => load());

  const activeReq = history.find((r) => r.status === "approved");
  const pendingReq = history.find((r) => r.status === "pending");

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedPlan) return;
    setSubmitting(true);
    try {
      await api.post("/subscriptions/request", {
        plan_id: selectedPlan.id,
        institute_name: form.institute_name || undefined,
        owner_name: form.owner_name || undefined,
        mobile: form.mobile || undefined,
        notes: form.notes || undefined,
      });
      toast.success("Request submitted — admin will review shortly");
      setSelectedPlan(null);
      setForm({ institute_name: "", owner_name: user?.name || "", mobile: "", notes: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const remainingDays = (r) => {
    if (!r?.expires_at) return null;
    const ms = new Date(r.expires_at).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  };

  return (
    <div>
      <PageHeader
        title="Subscription plans"
        subtitle="Request a plan below — an administrator will approve and activate it."
      />

      {/* Active or pending status card */}
      {(activeReq || pendingReq) && (() => {
        const r = activeReq || pendingReq;
        const meta = STATUS[r.status] || STATUS.pending;
        const Icon = meta.icon;
        const days = remainingDays(r);
        return (
          <div data-testid="my-subscription-status" className={`mb-6 rounded-xl border p-5 ${meta.color}`}>
            <div className="flex items-start gap-4">
              <Icon className="h-6 w-6 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-heading font-bold text-lg">{meta.label}</div>
                <div className="mt-1 text-sm">
                  <span className="font-semibold">{r.plan_name}</span> · {formatCurrency(r.amount, r.currency)} / {r.duration_days} days
                </div>
                <div className="mt-1 text-xs opacity-80">
                  Requested {new Date(r.created_at).toLocaleDateString()}
                  {r.activated_at && <> · Activated {new Date(r.activated_at).toLocaleDateString()}</>}
                  {r.expires_at && <> · Expires {new Date(r.expires_at).toLocaleDateString()} {days != null && `(${days} days left)`}</>}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Plan cards */}
      {!selectedPlan && (
        <div className="grid md:grid-cols-3 gap-5">
          {plans.map((p, i) => {
            const highlight = i === 1;
            return (
              <div key={p.id} data-testid={`subscribe-plan-${p.id}`}
                className={`relative rounded-2xl p-6 border ${highlight ? "bg-blue-600 text-white border-blue-600 shadow-2xl shadow-blue-600/30" : "bg-white border-slate-200 ym-card-hover"}`}>
                {highlight && <span className="absolute -top-3 right-6 px-3 py-1 rounded-full bg-yellow-400 text-slate-900 text-xs font-bold uppercase">Best value</span>}
                <h3 className="font-heading font-bold text-lg">{p.name}</h3>
                <div className="mt-3 flex items-end gap-1">
                  <span className="font-heading font-black text-4xl">{formatCurrency(p.amount, p.currency)}</span>
                  <span className={`text-sm ${highlight ? "text-blue-100" : "text-slate-500"}`}>/ {p.duration_days >= 365 ? "year" : "month"}</span>
                </div>
                <ul className="mt-5 space-y-2 text-sm">
                  <li className="flex gap-2 items-start"><Check className={`h-4 w-4 mt-0.5 ${highlight ? "text-yellow-300" : "text-blue-600"}`} /> Full access to platform</li>
                  <li className="flex gap-2 items-start"><Sparkles className={`h-4 w-4 mt-0.5 ${highlight ? "text-yellow-300" : "text-blue-600"}`} /> AI insights & report cards</li>
                  <li className="flex gap-2 items-start"><Check className={`h-4 w-4 mt-0.5 ${highlight ? "text-yellow-300" : "text-blue-600"}`} /> {p.duration_days} days of access</li>
                </ul>
                <button onClick={() => setSelectedPlan(p)} disabled={!!pendingReq} data-testid={`subscribe-request-${p.id}`}
                  className={`mt-6 w-full px-5 py-3 rounded-full font-semibold transition-colors disabled:opacity-60 ${
                    highlight ? "bg-white text-blue-600 hover:bg-slate-100" : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}>
                  {pendingReq ? "Request pending" : "Request this plan"}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Request form */}
      {selectedPlan && (
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6" data-testid="request-form">
          <h3 className="font-heading font-bold text-lg text-slate-900">Request: {selectedPlan.name}</h3>
          <p className="text-sm text-slate-600 mt-1">{formatCurrency(selectedPlan.amount, selectedPlan.currency)} / {selectedPlan.duration_days} days. Once you submit, our admin will review and activate.</p>
          <form onSubmit={submit} className="mt-5 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-700">Owner name</label>
              <input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                data-testid="req-owner" className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Institute name</label>
              <input value={form.institute_name} onChange={(e) => setForm({ ...form, institute_name: e.target.value })}
                data-testid="req-institute" className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Mobile number</label>
              <input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                data-testid="req-mobile" className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Notes (optional)</label>
              <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
            <div className="sm:col-span-2 flex gap-2">
              <button type="submit" disabled={submitting} data-testid="req-submit"
                className="px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60">
                {submitting ? "Submitting…" : "Submit request"}
              </button>
              <button type="button" onClick={() => setSelectedPlan(null)}
                className="px-5 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">
                Back to plans
              </button>
            </div>
          </form>
        </div>
      )}

      {history.length > 1 && (
        <div className="mt-8">
          <h3 className="font-heading font-bold text-lg text-slate-900 mb-3">Request history</h3>
          <div className="space-y-2">
            {history.map((r) => {
              const meta = STATUS[r.status] || STATUS.pending;
              return (
                <div key={r.id} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{r.plan_name} · {formatCurrency(r.amount, r.currency)}</div>
                    <div className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                  <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${meta.color.replace(' border-', ' border-')}`}>{meta.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
